import React, { FC, useMemo, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import useGetUserInformation from 'src/store/hooks/useGetUserInformation';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Box from '@amzn/meridian/box';
import { FloorplanProvider } from '@amzn/ihm-floorplan-viewer';
import getMidwayRedirectRoutes from 'src/utils/midwayRedirectRoutesHelper';
import { CONTACT_EMAIL } from 'src/constants/strings';
import AgileHome from '@components/agile/AgileHome';
import AgileManagement from '@components/agile/AgileManagement';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import AgileURLParameterTypes from '@components/agile/types/AgileURLParameterTypes';
import AssortmentSearch from '@components/agile/AssortmentSearch';
import ProductReadiness from '@components/productReadiness/ProductReadiness';
import WelcomePage from '@components/agile/welcomePage';
import ProductGroupsPage from 'src/pages/ProductGroupsPage';
import ZoneMap from './agile/ZoneMap';
import ZoneViewer from './agile/ZoneViewer';
import { SwitchButtonValue } from './agile/types/zoneMapType';
import AgileContext from './agile/context/AgileContext';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import AgileManagementV2 from '@components/agile/AgileManagementV2';

const Component: FC = () => {
  const urlParams: AgileURLParameterTypes = {
    storeId: (NavigationHelper.getQueryParam('storeId') as string) ?? '',
    date: (NavigationHelper.getQueryParam('date') as string) ?? '',
    zoneId: (NavigationHelper.getQueryParam('zoneId') as string) ?? '',
    demo: NavigationHelper.getQueryParam('demo') ?? undefined,
  };

  const [urlParameter, setUrlParameter] = useState<AgileURLParameterTypes>(urlParams);
  const [switchButtonValue, setSwitchButtonValue] = useState<SwitchButtonValue | undefined>(
    SwitchButtonValue.ViewZoneMap,
  );
  const [enableZoneListInHeader, setEnableZoneListInHeader] = useState(true);

  const { isError, isLoading } = useGetUserInformation();

  const agileContextValue = useMemo(
    () => ({
      enableZoneListInHeader,
      setEnableZoneListInHeader,
    }),
    [enableZoneListInHeader],
  );
  const nextGenEnabled = useFeatureFlag(FeatureName.NEXT_GEN_V2);
  if (isLoading) {
    return (
      <Box>
        <Loader data-cy="user_info_loading" />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box data-cy="user_info_loading_failed">
        Failed to load user information. Please log into&nbsp;
        <Link href={getMidwayRedirectRoutes(('beta' as string).toLowerCase())}>Midway.</Link>
        If you continue to see this message, please reach out to&nbsp;
        <Link href={`mailto: ${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</Link> to grant you permission for access.
      </Box>
    );
  }
  return (
    <div>
      <AgileContext.Provider value={agileContextValue}>
        <Routes>
          <Route path="/fresh" element={<AgileHome />}>
            <Route
              path="agileManagement"
              element={
                nextGenEnabled ? (
                  <AgileManagementV2 urlParams={urlParameter} setUrlParameter={setUrlParameter} />
                ) : (
                  <AgileManagement urlParams={urlParameter} setUrlParameter={setUrlParameter} />
                )
              }
            />
            <Route
              path="productReadiness"
              element={<ProductReadiness urlParams={urlParams} setUrlParameter={setUrlParameter} />}
            />
            <Route
              path="zoneCreation"
              element={
                <FloorplanProvider>
                  <ZoneViewer
                    urlParams={urlParams}
                    setUrlParameter={setUrlParameter}
                    switchButtonValue={switchButtonValue}
                    setSwitchButtonValue={setSwitchButtonValue}
                  />
                </FloorplanProvider>
              }
            />
            <Route path="zoneMap" element={<ZoneMap urlParams={urlParams} setUrlParameter={setUrlParameter} />} />
            <Route
              path="assortmentSearch"
              element={<AssortmentSearch urlParams={urlParameter} setUrlParameter={setUrlParameter} />}
            />
            <Route path="productGroups" element={<ProductGroupsPage />} />
            <Route path="" element={<WelcomePage />} />
          </Route>
          <Route path="/wholefoods" element={<AgileHome />}>
            <Route
              path="agileManagement"
              element={<AgileManagement urlParams={urlParameter} setUrlParameter={setUrlParameter} />}
            />
            <Route
              path="zoneCreation"
              element={
                <FloorplanProvider>
                  <ZoneViewer
                    urlParams={urlParams}
                    setUrlParameter={setUrlParameter}
                    switchButtonValue={switchButtonValue}
                    setSwitchButtonValue={setSwitchButtonValue}
                  />
                </FloorplanProvider>
              }
            />
            <Route path="zoneMap" element={<ZoneMap urlParams={urlParams} setUrlParameter={setUrlParameter} />} />
            <Route
              path="assortmentSearch"
              element={<AssortmentSearch urlParams={urlParameter} setUrlParameter={setUrlParameter} />}
            />
            <Route path="productGroups" element={<ProductGroupsPage />} />
            <Route path="" element={<WelcomePage />} />
          </Route>
          <Route path="/go" element={<AgileHome />}>
            <Route
              path="productReadiness"
              element={<ProductReadiness urlParams={urlParams} setUrlParameter={setUrlParameter} />}
            />
            <Route path="" element={<WelcomePage />} />
          </Route>
          <Route path="/gfc" element={<AgileHome />}>
            <Route path="productGroups" element={<ProductGroupsPage />} />
            <Route
              path="zoneCreation"
              element={
                <FloorplanProvider>
                  <ZoneViewer
                    urlParams={urlParams}
                    setUrlParameter={setUrlParameter}
                    switchButtonValue={switchButtonValue}
                    setSwitchButtonValue={setSwitchButtonValue}
                  />
                </FloorplanProvider>
              }
            />
            <Route path="" element={<WelcomePage />} />
          </Route>
          <Route path="/gfceu" element={<AgileHome />}>
            <Route path="productGroups" element={<ProductGroupsPage />} />
            <Route path="" element={<WelcomePage />} />
          </Route>
          <Route path="/*" element={<Navigate to="/fresh/zoneCreation" />} />
        </Routes>
      </AgileContext.Provider>
    </div>
  );
};

export default Component;
